<template>
  <div>
    <div v-if="label" class="tw-mb-1">
      <label :for="id || undefined" class="tw-block tw-text-xs dark:tw-text-white">{{ label }}</label>
    </div>
    <div
      :id="id"
      ref="triggerEl"
      role="combobox"
      :aria-expanded="isOpen"
      :aria-disabled="disabled"
      tabindex="0"
      class="studio-combo-select tw-mt-1 tw-flex tw-w-full tw-items-center tw-rounded-md tw-border tw-bg-white tw-px-3 tw-py-2 tw-text-sm dark:tw-bg-black"
      :class="{
        'tw-cursor-pointer hover:tw-border-deep-orange-500': !isOpen && !disabled,
        'tw-cursor-not-allowed tw-opacity-50': disabled,
        'tw-border-transparent tw-ring-2 tw-ring-deep-orange-500': isOpen
      }"
      @click="open"
      @keydown.enter.space.prevent="open"
    >
      <span :class="{ 'tw-text-slate-400 dark:tw-text-slate-500': !displayLabel }">
        {{ displayLabel || placeholder }}
      </span>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownEl"
        v-studio-click-outside="close"
        class="tw-fixed tw-z-50 tw-flex tw-max-h-[300px] tw-min-w-[220px] tw-flex-col tw-overflow-y-auto tw-rounded tw-border tw-border-slate-100 tw-bg-white tw-pb-1 tw-shadow-sm dark:tw-border-slate-700 dark:tw-bg-black"
      >
        <div class="tw-sticky tw-top-0 tw-bg-white tw-px-3 tw-pb-2 tw-pt-3 dark:tw-bg-black">
          <div
            class="tw-flex tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-bg-white tw-px-2 focus-within:tw-border-deep-orange-600 dark:tw-bg-black"
          >
            <TablerSearch class="tw-h-4 tw-w-4 tw-flex-none tw-text-gray-400" />
            <input
              ref="searchInputEl"
              v-model="searchTerm"
              type="text"
              placeholder="Search or type custom value"
              class="tw-h-8 tw-w-full tw-border-none tw-bg-transparent tw-text-sm focus:tw-outline-none focus:tw-ring-0 dark:tw-text-white"
              @input="onSearchInput"
            />
          </div>
        </div>

        <div
          v-if="displayOptions.length === 0"
          class="tw-grid tw-place-items-center tw-py-4 tw-text-xs tw-text-slate-500"
        >
          No options found
        </div>

        <template v-else>
          <div
            v-for="opt in displayOptions"
            :key="opt.isCustom ? `__custom__${opt.value}` : opt.value"
            class="tw-cursor-pointer tw-py-2 tw-pl-4 tw-pr-3 tw-text-xs hover:tw-bg-slate-100 hover:tw-text-deep-orange-500"
            :class="{
              'tw-bg-slate-100 tw-text-deep-orange-500 dark:tw-bg-slate-900': !opt.isCustom && opt.value === value
            }"
            @click="select(opt.value)"
          >
            <template v-if="opt.isCustom">Use "{{ opt.value }}"</template>
            <template v-else>{{ opt.label }}</template>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';

import TablerSearch from './Icons/tabler/TablerSearch.vue';

export type ComboSelectOption = {
  value: string;
  label: string;
};

type InternalOption = ComboSelectOption & { isCustom?: boolean };

const props = withDefaults(
  defineProps<{
    id?: string;
    label?: string;
    placeholder?: string;
    options?: ComboSelectOption[];
    value?: string;
    disabled?: boolean;
    integerOnly?: boolean;
  }>(),
  {
    id: '',
    label: '',
    placeholder: 'Select or type a value',
    options: () => [],
    value: '',
    disabled: false,
    integerOnly: false
  }
);

const emit = defineEmits<{
  change: [value: string];
}>();

const isOpen = ref(false);
const searchTerm = ref('');
const triggerEl = ref<HTMLElement | null>(null);
const dropdownEl = ref<HTMLElement | null>(null);
const searchInputEl = ref<HTMLInputElement | null>(null);

const displayLabel = computed(() => props.options.find(o => o.value === props.value)?.label ?? props.value);

const displayOptions = computed((): InternalOption[] => {
  const trimmed = searchTerm.value.trim();
  const term = trimmed.toLowerCase();
  const filtered = term
    ? props.options.filter(o => o.label.toLowerCase().includes(term) || o.value.toLowerCase().includes(term))
    : props.options;

  if (term) {
    const exactMatch = props.options.find(o => o.value === trimmed);
    const isValidCustom = !exactMatch && (!props.integerOnly || /^[1-9]\d*$/.test(trimmed));
    if (isValidCustom) {
      return [{ value: trimmed, label: trimmed, isCustom: true }, ...filtered];
    }
  }
  return filtered;
});

function open() {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
  searchTerm.value = '';
}

function onSearchInput() {
  if (!props.integerOnly) {
    return;
  }
  searchTerm.value = searchTerm.value.replace(/[^\d]/g, '').replace(/^0+/, '');
}

function select(val: string) {
  emit('change', val);
  close();
}

function repositionDropdown() {
  const trigger = triggerEl.value;
  const dropdown = dropdownEl.value;
  if (!trigger || !dropdown) {
    return;
  }
  const rect = trigger.getBoundingClientRect();
  const gap = 4;
  dropdown.style.width = `${trigger.offsetWidth}px`;
  dropdown.style.left = `${rect.left}px`;
  const spaceBelow = window.innerHeight - rect.bottom;
  dropdown.style.top =
    spaceBelow > dropdown.offsetHeight ? `${rect.bottom + gap}px` : `${rect.top - dropdown.offsetHeight - gap}px`;
}

watch(isOpen, opened => {
  if (opened) {
    nextTick(() => {
      // requestAnimationFrame ensures the dropdown is fully laid out before
      // reading offsetHeight for above/below placement decision
      requestAnimationFrame(repositionDropdown);
      searchInputEl.value?.focus();
    });
  }
});
</script>

<style lang="scss">
.studio-combo-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
